import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../Context/ThemeContext';
import { useAuth, SessionLoading, SignOutButton } from './AuthProvider';
import { safeReturnTo, rememberSsoDestination, takeSsoDestination } from './routes';
import './Auth.css';

function AuthFrame({ title, children }) {
  const { theme, toggleTheme } = useTheme();
  return <main className="auth-page"><section className="auth-card">
    <div className="auth-brand"><span>Cobygo</span><button className="auth-link-button" onClick={toggleTheme}>Use {theme === 'dark' ? 'light' : 'dark'} theme</button></div>
    <h1>{title}</h1>{children}
  </section></main>;
}

export function LoginPage() {
  const auth = useAuth(); const location = useLocation(); const navigate = useNavigate();
  const platform = auth.type === 'platform';
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [step, setStep] = useState(platform ? 'password' : 'email');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const target = safeReturnTo(location.state?.returnTo, auth.type);
  const reason = location.state?.reason || auth.reason;
  const ssoFailed = new URLSearchParams(location.search).get('error') === 'sso_failed';
  useEffect(() => { if (ssoFailed) takeSsoDestination(); }, [ssoFailed]);
  if (auth.status === 'loading') return <SessionLoading />;
  if (auth.status === 'authenticated') return <Navigate replace to={target} />;
  const submit = async event => {
    event.preventDefault(); setError(''); setBusy(true);
    try {
      if (step === 'email') {
        const discovery = await auth.session.discover(email.trim());
        if (discovery.status === 'PASSWORD_SETUP_REQUIRED') { setStep('setup'); return; }
        if (discovery.status !== 'EXISTING_USER') throw new Error('Unavailable');
        if (discovery.authenticationType === 'SSO') {
          const route = target.startsWith('/app/admin') ? '/app/admin' : '/app/tools';
          const response = await auth.session.startSso(email.trim(), route);
          const authorization = new URL(response.authorizationUrl);
          if (authorization.protocol !== 'https:' || authorization.username || authorization.password) throw new Error('Unsafe authorization destination');
          rememberSsoDestination(target);
          window.location.assign(authorization.href);
          return;
        }
        if (discovery.authenticationType !== 'LOCAL') throw new Error('Unavailable');
        setStep('password');
      } else {
        await auth.session.login(email.trim(), password);
        setPassword(''); navigate(target, { replace: true });
      }
    } catch {
      setPassword('');
      setError('Unable to sign in. Check your credentials or contact your administrator if your account or subscription is unavailable.');
    } finally { setBusy(false); }
  };
  return <AuthFrame title={platform ? 'Cobygo platform admin sign in' : 'Sign in to Cobygo'}>
    {ssoFailed && <p role="alert">Single sign-on failed. Please try again or contact your administrator.</p>}
    {reason === 'expired' && <p role="status">Your session ended. Please sign in again.</p>}
    {reason === 'logout_failed' && <p role="alert">You are signed out here, but server sign-out could not be confirmed. Close this browser session before sharing this device.</p>}
    {error && <p role="alert">{error}</p>}
    {step === 'setup' ? <><p>Your administrator must share your secure one-time password setup link or token. No email or verification code is sent here.</p><Link to="/password/setup">Set up password</Link>
      <button className="auth-link-button" onClick={() => setStep('email')}>Use another email</button></> :
      <form onSubmit={submit}>
        <label htmlFor="auth-email">Email</label><input id="auth-email" type="email" autoComplete="username" required value={email} disabled={busy || (!platform && step === 'password')} onChange={e => setEmail(e.target.value)} />
        {step === 'password' && <><label htmlFor="auth-password">Password</label><input id="auth-password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} /></>}
        <button className="auth-primary" type="submit" disabled={busy}>{busy ? 'Please wait…' : step === 'email' ? 'Continue' : 'Sign in'}</button>
        {!platform && step === 'password' && <button className="auth-link-button" type="button" onClick={() => { setPassword(''); setStep('email'); }}>Use another email</button>}
      </form>}
    {!platform && <p><Link to="/password/setup">Have a password setup token?</Link></p>}
    {platform ? <p>Signing in to your organization? <Link to="/login">Tenant sign in</Link></p> :
      <p>For Cobygo platform operators: <Link to="/platform-admin/login">Platform Admin sign in</Link></p>}
  </AuthFrame>;
}

export function PasswordSetupPage() {
  const { session } = useAuth();
  // Setup credentials are distinct from access/refresh tokens. Consume a shared link's
  // fragment into component memory, then immediately scrub it from browser history.
  const [token, setToken] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('setup') || '');
  const [password, setPassword] = useState(''); const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false); const [complete, setComplete] = useState(false); const [error, setError] = useState('');
  useEffect(() => { window.history.replaceState(window.history.state, '', window.location.pathname); }, []);
  const submit = async event => {
    event.preventDefault(); setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try { await session.setupPassword(token.trim(), password); setToken(''); setPassword(''); setConfirm(''); setComplete(true); }
    catch { setError('Password setup failed. The one-time token may be invalid, used or expired. Ask your administrator for assistance.'); }
    finally { setBusy(false); }
  };
  return <AuthFrame title="Set up your password">{complete ? <><p>Password created. You can now sign in.</p><Link to="/login">Go to sign in</Link></> : <>
    <p>Use the one-time setup token shared by your administrator. Tokens expire after 24 hours.</p>
    {error && <p role="alert">{error}</p>}<form onSubmit={submit}>
      <label htmlFor="setup-token">Setup token</label><input id="setup-token" type="password" autoComplete="off" required value={token} onChange={e => setToken(e.target.value)} />
      <label htmlFor="new-password">New password</label><input id="new-password" type="password" autoComplete="new-password" required minLength={12} maxLength={128} value={password} onChange={e => setPassword(e.target.value)} />
      <label htmlFor="confirm-password">Confirm password</label><input id="confirm-password" type="password" autoComplete="new-password" required minLength={12} maxLength={128} value={confirm} onChange={e => setConfirm(e.target.value)} />
      <button className="auth-primary" disabled={busy}>{busy ? 'Saving…' : 'Set password'}</button>
    </form></>}</AuthFrame>;
}

export function TenantAdminSetupPage() {
  const { session } = useAuth();
  const location = useLocation();
  const [token, setToken] = useState(() => {
    const searchParams = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
    const hashParams = new URLSearchParams((location.hash || (typeof window !== 'undefined' ? window.location.hash : '')).slice(1));
    return searchParams.get('token') || hashParams.get('token') || '';
  });
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }, []);

  const submit = async event => {
    event.preventDefault();
    setError('');
    if (!token.trim()) {
      setError('An invitation token is required. Please check your setup link.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await session.acceptInvitation(token.trim(), password, displayName);
      setToken('');
      setPassword('');
      setConfirm('');
      setDisplayName('');
      setComplete(true);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(msg || 'Invitation acceptance failed. The one-time token may be invalid, used or expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Tenant administrator setup">
      {complete ? (
        <>
          <p>Tenant administrator account activated. You can now sign in.</p>
          <Link to="/login">Go to sign in</Link>
        </>
      ) : (
        <>
          <p>Complete your initial Tenant Admin account setup. This one-time link activates your organization.</p>
          {error && <p role="alert">{error}</p>}
          <form onSubmit={submit}>
            <label htmlFor="invitation-token">Invitation token</label>
            <input
              id="invitation-token"
              type="password"
              autoComplete="off"
              required
              value={token}
              disabled={busy}
              onChange={e => setToken(e.target.value)}
            />
            <label htmlFor="invitation-displayName">Display name (optional)</label>
            <input
              id="invitation-displayName"
              type="text"
              autoComplete="name"
              maxLength={200}
              value={displayName}
              disabled={busy}
              onChange={e => setDisplayName(e.target.value)}
            />
            <label htmlFor="invitation-password">New password</label>
            <input
              id="invitation-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              maxLength={128}
              value={password}
              disabled={busy}
              onChange={e => setPassword(e.target.value)}
            />
            <label htmlFor="invitation-confirm">Confirm password</label>
            <input
              id="invitation-confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              maxLength={128}
              value={confirm}
              disabled={busy}
              onChange={e => setConfirm(e.target.value)}
            />
            <button className="auth-primary" disabled={busy}>
              {busy ? 'Activating…' : 'Complete setup'}
            </button>
          </form>
          <p><Link to="/login">Already completed setup? Sign in</Link></p>
        </>
      )}
    </AuthFrame>
  );
}

// Protected entry points only. Administration functionality belongs to later phases.
export function AdminEntry({ platform = false }) {
  return <AuthFrame title={platform ? 'Cobygo platform administration' : 'Tenant administration'}>
    <p>You are signed in. Administration features are not part of this phase.</p>
    {!platform && <p><Link to="/app/tools">Developer tools</Link></p>}<SignOutButton />
  </AuthFrame>;
}
